import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Phone, ArrowRight, Sparkles, GraduationCap, CalendarDays, Users, ShieldCheck, Bell } from "lucide-react";
import { SCHOOL, IMAGES, WHY_CHOOSE, ACADEMIC_STAGES } from "@/lib/constants";
import { Icon } from "@/components/Icon";
import { SectionHeading } from "@/components/SectionHeading";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const Stat = ({ value, label, icon: I }) => (
  <div className="flex items-center gap-3">
    <div className="h-12 w-12 rounded-xl bg-white/10 grid place-items-center text-gold"><I className="h-6 w-6" /></div>
    <div>
      <div className="font-head font-extrabold text-2xl text-white">{value}</div>
      <div className="text-xs text-slate-300">{label}</div>
    </div>
  </div>
);

export default function Home() {
  const [notices, setNotices] = useState([]);
  useEffect(() => { api.getNotices().then((r) => setNotices(r.data)).catch(() => {}); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="B.R.Smart Academy campus" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 rounded-full bg-brandred px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white"
            >
              <Sparkles className="h-4 w-4" /> Admissions Open {SCHOOL.session}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="mt-5 font-head text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-white"
            >
              Where Little Minds <span className="text-gold">Grow Bright</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-5 text-lg text-slate-200 max-w-xl leading-relaxed"
            >
              Welcome to <b className="text-white">B.R.Smart Kidz / Academy</b> — a CBSE, English medium school in Kolkata offering classes from Pre-Nursery to Class 10. Nurturing curious, confident and caring learners since {SCHOOL.estYear}.
            </motion.p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/admission" data-testid="hero-apply-btn">
                <Button className="bg-brandred hover:bg-brandred/90 text-white rounded-full px-7 h-12 font-semibold text-base">
                  Apply for Admission <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
              <a href={`tel:${SCHOOL.phones[0]}`} data-testid="hero-call-btn">
                <Button variant="outline" className="rounded-full px-7 h-12 font-semibold text-base bg-white/10 text-white border-white/30 hover:bg-white hover:text-navy">
                  <Phone className="mr-1.5 h-4 w-4" /> Call Now
                </Button>
              </a>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-lg">
              <Stat value="2018" label="Established" icon={CalendarDays} />
              <Stat value="14+" label="Classes Offered" icon={GraduationCap} />
              <Stat value="100%" label="Individual Care" icon={Users} />
            </div>
          </div>

          {/* Enquiry card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.25 }}
            className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border border-slate-100"
          >
            <h3 className="font-head font-bold text-xl text-navy">Quick Enquiry</h3>
            <p className="text-sm text-slate-500 mb-4">Fill the form & our team will call you back.</p>
            <EnquiryForm />
          </motion.div>
        </div>

        {/* Notice ticker */}
        {notices.length > 0 && (
          <div className="relative bg-gold text-navy py-2.5 flex items-center">
            <span className="hidden sm:flex items-center gap-1.5 font-bold text-sm px-4 shrink-0"><Bell className="h-4 w-4" /> LATEST:</span>
            <Marquee gradient={false} speed={45} pauseOnHover>
              {notices.map((n) => (
                <span key={n.id} className="mx-8 text-sm font-medium">• {n.title}</span>
              ))}
            </Marquee>
          </div>
        )}
      </section>

      {/* WELCOME / ABOUT SNAPSHOT */}
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <img src={IMAGES.students} alt="Happy students" className="rounded-3xl shadow-xl w-full object-cover" />
          <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-navy text-white rounded-2xl p-5 shadow-xl max-w-[220px]">
            <ShieldCheck className="h-7 w-7 text-gold mb-2" />
            <p className="text-sm font-semibold leading-snug">Safe, secure & CCTV-monitored campus</p>
          </div>
        </div>
        <div>
          <SectionHeading label="Welcome to Our School" title="A caring home for happy, confident learners" />
          <p className="text-slate-600 leading-relaxed">
            At B.R.Smart Kidz / Academy, we blend a warm, nurturing environment with a modern CBSE curriculum. From playful Montessori beginnings to rigorous secondary studies, every child receives individual attention, smart-class learning and values that last a lifetime.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            Proudly supported by <b className="text-navy">{SCHOOL.foundation}</b> (established {SCHOOL.foundationYear}), we are committed to accessible, quality education for our community.
          </p>
          <Link to="/about" data-testid="home-about-link">
            <Button className="mt-6 bg-navy hover:bg-navy/90 text-white rounded-full px-6">Read More About Us <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE US - BENTO */}
      <section className="bg-[#F3F4F6] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading center label="Why Choose Us" title="Everything your child needs to thrive" desc="Ten reasons parents across Kolkata trust B.R.Smart Academy." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_CHOOSE.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }}
                className={`group bg-white rounded-2xl p-6 border border-slate-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-transform ${i === 0 ? "lg:col-span-2 lg:row-span-1" : ""}`}
              >
                <div className="h-12 w-12 rounded-xl grid place-items-center mb-4 bg-brandred/10 text-brandred group-hover:bg-brandred group-hover:text-white transition-colors">
                  <Icon name={f.icon} className="h-6 w-6" />
                </div>
                <h3 className="font-head font-bold text-navy text-lg">{f.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ACADEMIC STAGES */}
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeading center label="Academics" title="A learning journey from age 2 to Class 10" />
        <div className="grid md:grid-cols-3 gap-6">
          {ACADEMIC_STAGES.map((s) => (
            <div key={s.key} className="rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-lg transition-transform hover:-translate-y-1">
              <div className="h-2" style={{ backgroundColor: s.color }} />
              <div className="p-7">
                <h3 className="font-head font-extrabold text-2xl text-navy">{s.name}</h3>
                <p className="text-sm font-semibold mt-1" style={{ color: s.color }}>{s.range}</p>
                <p className="text-slate-600 mt-4 leading-relaxed text-sm">{s.desc}</p>
                <Link to="/academics" className="inline-flex items-center gap-1 mt-5 text-sm font-bold text-navy hover:text-brandred" data-testid={`home-stage-${s.key}`}>
                  Explore <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-navy overflow-hidden">
        <img src={IMAGES.smartClass} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-head text-3xl sm:text-4xl lg:text-5xl font-black text-white">Give your child a bright beginning</h2>
          <p className="text-slate-200 mt-4 text-lg">Admissions for session {SCHOOL.session} are now open. Limited seats — apply today!</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/admission" data-testid="cta-apply-btn"><Button className="bg-brandred hover:bg-brandred/90 text-white rounded-full px-8 h-12 font-semibold">Apply Online</Button></Link>
            <Link to="/contact" data-testid="cta-visit-btn"><Button variant="outline" className="rounded-full px-8 h-12 font-semibold bg-white/10 text-white border-white/30 hover:bg-white hover:text-navy">Visit Campus</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
